/**
 * Storefront side of the GTM setup (snippets/1scp-tracking.liquid, docs/TRACKING.md).
 * Syncs Consent Mode with Shopify's cookie banner, then pushes view_item and add_to_cart.
 * Checkout and purchase come from the Shopify custom pixel, because the checkout runs no theme code.
 */
(() => {
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const gtag = function () {
    dataLayer.push(arguments);
  };
  const currency = () => window.Shopify?.currency?.active || "EUR";
  const state = (allowed) => (allowed ? "granted" : "denied");

  let ready = false;
  const queue = [];

  const push = (event, ecommerce) => {
    const entry = { event, ecommerce };
    if (!ready) return queue.push(entry);
    dataLayer.push({ ecommerce: null });
    dataLayer.push(entry);
  };

  const applyConsent = (analytics, marketing) => {
    gtag("consent", "update", {
      analytics_storage: state(analytics),
      ad_storage: state(marketing),
      ad_user_data: state(marketing),
      ad_personalization: state(marketing),
    });
  };

  const start = () => {
    if (ready) return;
    ready = true;
    dataLayer.push({ event: "scpd_consent_ready" });
    for (const entry of queue.splice(0)) entry.raw ? dataLayer.push(entry.raw) : push(entry.event, entry.ecommerce);
  };

  const readPrivacy = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return false;
    applyConsent(privacy.analyticsProcessingAllowed(), privacy.marketingAllowed());
    return true;
  };

  document.addEventListener("visitorConsentCollected", (event) => {
    applyConsent(event.detail?.analyticsAllowed, event.detail?.marketingAllowed);
    dataLayer.push({ event: "scpd_consent_update" });
  });

  // If the consent API never answers, tags still start, on the denied defaults.
  const fallback = setTimeout(start, 3000);
  const loadPrivacy = () => {
    if (!window.Shopify?.loadFeatures) return start();
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (!error) readPrivacy();
      clearTimeout(fallback);
      start();
    });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", loadPrivacy);
  else loadPrivacy();

  const pageItem = document.getElementById("scpd-tracking-item");
  if (pageItem) {
    try {
      const item = JSON.parse(pageItem.textContent);
      push("view_item", { currency: currency(), value: item.price, items: [{ ...item, quantity: 1 }] });
    } catch (error) {
      console.error(error);
    }
  }

  const pushLead = (form) => {
    const tags = form?.querySelector("[name='contact[tags]']")?.value || "";
    const item = pageItem ? JSON.parse(pageItem.textContent) : null;
    const lead = {
      event: "generate_lead",
      lead_type: /waitlist/.test(tags) ? "waitlist" : "newsletter",
      form_name: form?.id || "",
      item_name: /waitlist/.test(tags) ? item?.item_name : undefined,
    };
    if (ready) dataLayer.push(lead);
    else queue.push({ raw: lead });
  };

  // The waitlist signs up with fetch and stays on the page.
  document.addEventListener("scpd:waitlist-joined", (event) => {
    pushLead(event.detail?.form);
  });

  // Every other customer form reloads to `?customer_posted=true#<form id>`. A refresh keeps that URL,
  // so the sign-up is remembered for the session and counted once.
  if (new URLSearchParams(location.search).get("customer_posted") === "true") {
    const form = location.hash && document.getElementById(location.hash.slice(1));
    let seen = false;
    try {
      seen = sessionStorage.getItem("scpd-lead") === location.href;
      sessionStorage.setItem("scpd-lead", location.href);
    } catch (error) {}
    if (form && !seen) pushLead(form);
  }

  /**
   * Every add to the bag (product form, bag upsells, the free scoop) goes through /cart/add, so the
   * request is watched here instead of in each component. The response holds the whole line, whose
   * quantity can include earlier adds, so the quantity is taken from the request.
   */
  const requestedQuantities = (body) => {
    const quantities = new Map();
    try {
      if (body instanceof FormData) {
        const id = body.get("id");
        if (id) quantities.set(String(id), Number(body.get("quantity")) || 1);
      } else if (typeof body === "string") {
        const json = JSON.parse(body);
        const items = json.items || (json.id ? [json] : []);
        for (const item of items) quantities.set(String(item.id), Number(item.quantity) || 1);
      }
    } catch (error) {
      // An unreadable body only loses the quantity; the add is still tracked with the line's own.
    }
    return quantities;
  };

  const toItem = (line, quantities) => ({
    item_id: line.sku || String(line.variant_id),
    item_name: line.product_title,
    item_brand: line.vendor,
    item_category: line.product_type || undefined,
    item_variant: line.product_has_only_default_variant ? undefined : line.variant_title,
    variant_id: line.variant_id,
    product_id: line.product_id,
    price: (line.final_price ?? line.price) / 100,
    quantity: quantities.get(String(line.variant_id)) ?? line.quantity ?? 1,
  });

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const request = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === "string" ? input : input?.url || String(input);
      if (!/\/cart\/add(\.js)?$/.test(new URL(url, location.href).pathname)) return request;
      const quantities = requestedQuantities(init?.body);
      request
        .then((response) => (response.ok ? response.clone().json() : null))
        .then((body) => {
          if (!body) return;
          const lines = body.items || (body.variant_id ? [body] : []);
          if (!lines.length) return;
          const items = lines.map((line) => toItem(line, quantities));
          const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
          push("add_to_cart", { currency: currency(), value: Math.round(value * 100) / 100, items });
        })
        .catch(() => {});
    } catch (error) {
      // Tracking must never break the request it watches.
    }
    return request;
  };
})();
